import type { GlossaryTerm, GuideModule } from './types';

export function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export type GuideSearchResultKind = 'title' | 'step' | 'faq' | 'glossary';

export interface GuideSearchResult {
  kind: GuideSearchResultKind;
  moduleId: string;
  /** Empty for a `'glossary'` result, which does not belong to a module. */
  moduleTitle: string;
  label: string;
  snippet: string;
}

/** Priority order, most to least relevant — decides the ranking, not just the filter. */
const KIND_RANK: Record<GuideSearchResultKind, number> = { title: 0, step: 1, faq: 2, glossary: 3 };

export function searchGuide(
  query: string,
  modules: readonly GuideModule[],
  glossary: readonly GlossaryTerm[],
): GuideSearchResult[] {
  const needle = normalize(query.trim());
  if (needle === '') return [];

  const results: GuideSearchResult[] = [];

  for (const module of modules) {
    if (normalize(module.title).includes(needle)) {
      results.push({
        kind: 'title',
        moduleId: module.id,
        moduleTitle: module.title,
        label: module.title,
        snippet: module.summary,
      });
    }
    for (const step of module.steps) {
      if (normalize(step.title).includes(needle) || normalize(step.description).includes(needle)) {
        results.push({
          kind: 'step',
          moduleId: module.id,
          moduleTitle: module.title,
          label: step.title,
          snippet: step.description,
        });
      }
    }
    for (const item of module.faq) {
      if (normalize(item.question).includes(needle) || normalize(item.answer).includes(needle)) {
        results.push({
          kind: 'faq',
          moduleId: module.id,
          moduleTitle: module.title,
          label: item.question,
          snippet: item.answer,
        });
      }
    }
  }

  for (const entry of glossary) {
    if (normalize(entry.term).includes(needle) || normalize(entry.definition).includes(needle)) {
      results.push({
        kind: 'glossary',
        moduleId: '',
        moduleTitle: '',
        label: entry.term,
        snippet: entry.definition,
      });
    }
  }

  return results.sort((a, b) => KIND_RANK[a.kind] - KIND_RANK[b.kind]);
}
